import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import {
  retrySessionComposition,
  type SessionCompositionOptions,
  type SessionCompositionOutcome,
  type SessionCompositionRetrySelection,
} from "@/shared/domain/sessions";
import { shouldReportError } from "@/shared/http/shouldReportError";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseAddFilesToSessionVariables extends SessionCompositionOptions {
  outcome: SessionCompositionOutcome;
  retrySelection?: SessionCompositionRetrySelection;
}

/**
 * Add a prepared selection of files to a session that already exists, or resend part of an earlier
 * attempt. Returns one entry per selected file (included, refused, or unknown) rather than
 * rejecting on the first refusal, so a caller always learns the fate of every file it picked. A
 * transport failure or an unreadable answer is reported on the returned outcome's `stopCause`, not
 * by rejecting. The mutation rejects before sending anything when the outcome names no session or a
 * `retrySelection` this outcome cannot satisfy, and at any point when the caller's `stopRequested` or
 * `onProgress` throws.
 *
 * Invalidation lives in the mutation function, not `onSuccess`, for two reasons:
 * 1. A part-applied add still changed the session, and a rejected mutation never reaches `onSuccess`.
 * 2. It targets the session captured when the call started, so a caller that reassigns its variables
 *    object mid-flight cannot redirect the refresh at a session this call never touched.
 *
 * Invalidating `detail(sessionId)` covers the session's files and topics reads; `lists()` covers the
 * session lists, whose rows carry aggregate bounds the added files may have widened.
 */
export function useAddFilesToSession() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    SessionCompositionOutcome,
    Error,
    UseAddFilesToSessionVariables
  >({
    mutationFn: async ({
      outcome,
      retrySelection,
      stopRequested,
      onProgress,
    }) => {
      const sessionId = outcome.sessionId;
      let latestOutcome = outcome;
      try {
        const options: SessionCompositionOptions = {
          stopRequested,
          onProgress: (progress) => {
            latestOutcome = progress;
            onProgress?.(progress);
          },
        };
        latestOutcome = await retrySessionComposition(
          sessionService,
          outcome,
          retrySelection ?? { kind: "remaining" },
          options,
        );
        const cause = latestOutcome.stopCause;
        // A stop the user asked for carries no error. A request or protocol cause does, and since the
        // mutation resolves with it rather than rejecting, it is reported here.
        if (
          cause &&
          cause.kind !== "stopped" &&
          shouldReportError(cause.error)
        ) {
          try {
            ErrorMonitoringService.captureError(cause.error, {
              operationId: outcome.context.operationId,
              orgId: outcome.context.orgId,
              sessionId,
            });
          } catch {
            // Monitoring being down must not turn the per-file outcomes into a rejected mutation.
          }
        }
        return latestOutcome;
      } finally {
        // `hasDispatched` turns true before a request goes out, so a reply that never arrived still
        // refreshes: the server may have applied the batch anyway.
        if (latestOutcome.hasDispatched && sessionId !== undefined) {
          await Promise.allSettled([
            queryClient.invalidateQueries({
              queryKey: sessionQueryKeys.detail(sessionId),
            }),
            queryClient.invalidateQueries({
              queryKey: sessionQueryKeys.lists(),
            }),
          ]);
        }
      }
    },
  });
}
